import React from "react";
import { Box } from "@mui/material";
import Chart from "react-apexcharts";
import FleetFixedColumn from "./FleetFixedColumn";
import FleetChartWrapper from "./FleetChartWrapper";

/**
 * FleetScrollableChartWrapper - Wrapper for fleet charts with fixed Y-axes and scrollable plot area
 * @param {Object} chartOptions - ApexCharts options configuration
 * @param {Array} series - Chart series data
 * @param {string} chartType - Chart type (default: "bar")
 * @param {number} chartHeight - Height of the chart (default: 315)
 * @param {Object} leftAxis - Left Y-axis configuration: { label, labelData }
 * @param {Object} rightAxis - Right Y-axis configuration: { label, labelData }
 * @param {boolean} isSidebarExpanded - Whether the sidebar is expanded
 * @param {number} minWidth - Minimum width for the chart
 * @param {number} categoriesCount - Number of categories/data points
 * @param {boolean} drillDownEnabled - Whether drill-down is enabled
 */
const FleetScrollableChartWrapper = ({
    chartOptions,
    series,
    chartType = "bar",
    chartHeight = 315,
    leftAxis = null,
    rightAxis = null,
    isSidebarExpanded = false,
    minWidth = 800,
    categoriesCount = 0,
    drillDownEnabled = false,
}) => {
    const getMaxWidth = () => {
        if (isSidebarExpanded) return "calc(100vw - 420px)";
        return rightAxis ? "calc(100vw - 300px)" : "calc(100vw - 210px)";
    };

    return (
        <Box sx={{ display: "flex", position: "relative", width: "100%", overflow: "hidden" }}>
            {/* Left Y-Axis - Fixed */}
            {leftAxis && (
                <FleetFixedColumn
                    position="left"
                    label={leftAxis.label}
                    labelData={leftAxis.labelData}
                    chartHeight={chartHeight}
                />
            )}

            {/* Scrollable Chart Area */}
            <Box
                sx={{
                    flex: 1,
                    overflowX: "auto",
                    overflowY: "hidden",
                    position: "relative",
                    minWidth: 0,
                    maxWidth: getMaxWidth(),
                }}
            >
                <FleetChartWrapper
                    drillDownEnabled={drillDownEnabled}
                    sx={{
                        width: `max(100%, ${minWidth}px)`,
                        height: chartHeight,
                    }}
                >
                    <Chart
                        options={chartOptions}
                        series={series}
                        type={chartType}
                        height={chartHeight}
                        width="100%"
                    />
                </FleetChartWrapper>
            </Box>

            {/* Right Y-Axis - Fixed */}
            {rightAxis && (
                <FleetFixedColumn
                    position="right"
                    label={rightAxis.label}
                    labelData={rightAxis.labelData}
                    chartHeight={chartHeight}
                />
            )}
        </Box>
    );
};

export default FleetScrollableChartWrapper;